import { Injectable, OnDestroy } from '@angular/core';
import documentListingQuery from '@graphql/fund-document/fund-documents-all-products.graphql';
import { ADVISOR, Z_SHARECLASS } from '@literature/const/literature-data-const';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureFundDataService } from '@literature/services/literature-fund-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Product, ShareClass } from '@models';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { IUserProfile, ProfileService, SegmentService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  Audience,
  ConfigurationId,
  DocumentMatchCondition,
  FundId,
  KeyIdentifiers,
  LiteratureDocument,
  LiteratureDocumentListingData,
  LiteratureDocumentListingState,
  LiteratureListingConfiguration,
  NoYesFlag,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import capitalize from 'lodash/capitalize';
import { Observable, Subject, forkJoin } from 'rxjs';
import { filter, retry, switchMap, takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('SmarshLiteratureListingService');

@Injectable({
  providedIn: 'root',
})
export class SmarshLiteratureListingService implements OnDestroy {
  private literaturePageConfig: LiteratureListingConfiguration;
  isInternational: boolean;
  state: LiteratureDocumentListingState = { isError: false };
  documentListingData$ = new Subject<LiteratureDocumentListingState>();
  isIdentified: boolean;
  isLoggedIn: boolean;
  private listingData: LiteratureDocumentListingData[];
  private dataMap: Map<FundId, Product> = new Map();
  private fundUmbrellaMap: Map<string, [any]> = new Map<string, [any]>();
  public dateFormat: string;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private literatureConfigService: LiteratureGlobalConfigService,
    private fundDataService: LiteratureFundDataService,
    private literatureDataService: LiteratureDataService,
    protected translateService: TranslateService,
    protected profileService: ProfileService,
    protected segmentService: SegmentService
  ) {
    this.isInternational = this.literatureConfigService.isInternational;
    this.profileService
      .isLoggedIn$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      });
  }

  public populate(param: Map<string, string>): void {
    this.literaturePageConfig = this.literatureConfigService.getLiteratureListingConfig();
    // Audience new flow
    if (this.literatureConfigService.checkAudienceFromOverrideList()) {
      param.set(
        'audience',
        this.literaturePageConfig?.segmentOverrideList?.toString()
      );
    }
    if (this.isInternational) {
      forkJoin([
        this.fundDataService.register(
          documentListingQuery,
          {
            configurationName: ConfigurationId.MF,
            productType: ProductTypeParameter.MUTUAL_FUNDS,
          },
          this.literatureConfigService.literatureConfig?.activeList,
          this.literatureConfigService.literatureConfig?.fundOverrideFlag
        ),
        this.literatureDataService.loadData(param),
      ]).subscribe(
        (response) => {
          this.mergeData(response);
          this.documentListingData$.next(this.state);
        },
        (err) => {
          logger.error(
            '[populateLiteratureData] error while getting data from backend service : ' +
              JSON.stringify(err) +
              ' [error status]: ' +
              err.status
          );
          this.state.isError = true;
          this.state.data = [];
          this.documentListingData$.next(this.state);
        }
      );
    } else {
      this.literatureDataService
        .getProfileData$()
        ?.pipe(
          takeUntil(this.unsubscribe$),
          switchMap((profileData: IUserProfile) => {
            this.literatureDataService.setProfileData(profileData);
            return forkJoin([
              this.fundDataService.register(
                documentListingQuery,
                {
                  configurationName: ConfigurationId.MF,
                  productType: ProductTypeParameter.MUTUAL_FUNDS,
                },
                this.literatureConfigService.literatureConfig?.activeList,
                this.literatureConfigService.literatureConfig?.fundOverrideFlag
              ),
              this.literatureDataService.loadData(param, true),
            ]);
          }),
          retry(1)
        )
        .subscribe(
          (response) => {
            this.mergeData(response);
            this.documentListingData$.next(this.state);
          },
          (err) => {
            logger.error(
              '[populateLiteratureData] error while getting data from backend service : ' +
                JSON.stringify(err) +
                ' [error status]: ' +
                err.status
            );
            this.state.isError = true;
            this.state.data = [];
            this.documentListingData$.next(this.state);
          }
        );
    }
  }

  public register(): Observable<LiteratureDocumentListingState> {
    return this.documentListingData$.asObservable();
  }

  /**
   * Prepares data for literature listing page from PPSS data and literature data
   * @param gridData LiteratureDocumentListingData
   * @returns LiteratureDocumentListingData
   */
  private mergeData(gridData): LiteratureDocumentListingData[] {
    logger.debug('merge data: ', gridData);
    this.listingData = [];
    const [fundListing, litData] = gridData;
    this.dateFormat = this.literatureConfigService.dateFormat;

    this.productDataMergeInitializer(fundListing._products);
    const colToDisplay = this.literaturePageConfig?.documentTypeDisplay;
    const filters = [
      ...this.literaturePageConfig?.primaryFilters,
      ...this.literaturePageConfig?.additionalFilters,
    ];
    for (const literatureData of litData.document) {
      let litSet: Set<string>;
      const data = this.literatureDataService.literatureMapper(literatureData);

      // WDE-441 flag usePreviewLink informs components to redirect user to preview page instead download.
      data.usePreviewLink = this.literatureConfigService?.literatureConfig?.documentsWithPreview?.includes(
        data.dctermsType
      );

      if (
        !this.checkAudienceAndDocumentTypeConfiguration(
          data,
          filters,
          colToDisplay
        )
      ) {
        continue;
      }
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber.split(',')
        : [];
      if (!this.isInternational) {
        // when there is no association with funds need to show that documents also
        if (frkOneTISNumber.length === 0 && !data.frkFundGroupCode) {
          this.loadDocumentList(data, {}, this.listingData);
        }
        litSet = new Set<string>();
      }
      let matchFound = false;
      const matchOn = this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(data.dctermsType);
      switch (matchOn) {
        case DocumentMatchCondition.UNASSOCIATED:
          data.identifiers = this.initializeIdentifier();
          this.loadDocumentList(data, {}, this.listingData);
          break;
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_ID:
          matchFound = this.matchOnFundId(frkOneTISNumber, data, litSet);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_UMBRELLA:
          matchFound = this.matchOnFundUmbrella(data);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        default:
          if (!this.matchOnFundId(frkOneTISNumber, data, litSet)) {
            this.matchOnFundUmbrella(data);
          }
      }
    }

    this.state.data = this.listingData;
    return this.listingData;
  }

  private checkAudience(
    data: LiteratureDocument,
    segmentOverrideList: string[]
  ): boolean {
    let audienceFound = false;
    const audienceEnum = data.dctermsAudience?.map((key) => capitalize(key));
    const audiences = data.dctermsAudience?.map((key) => Audience[key]);
    if (segmentOverrideList?.length > 0) {
      for (const segment of segmentOverrideList) {
        if (audienceEnum?.includes(capitalize(segment))) {
          audienceFound = true;
        } else if (audiences?.includes(capitalize(segment))) {
          audienceFound = true;
        } else if (
          audienceEnum?.includes(
            capitalize(this.segmentService.getCurrentSegmentId())
          )
        ) {
          // WDE-5889
          audienceFound = true;
        }
        if (audienceFound) {
          break;
        }
      }
    } else {
      if (
        audienceEnum?.includes(
          capitalize(this.segmentService.getCurrentSegmentId())
        )
      ) {
        audienceFound = true;
      } else if (
        audiences?.includes(this.segmentService.getCurrentSegmentId())
      ) {
        audienceFound = true;
      }
    }
    return audienceFound;
  }

  private checkAudienceAndDocumentTypeConfiguration(
    data: LiteratureDocument,
    filters: string[],
    colToDisplay: string[]
  ): boolean {
    const isLanguageFilterConfigured = filters.indexOf('LANGUAGE') > -1;
    const languageFilterArray: string[] = this.literaturePageConfig
      ?.languageFilterArray;
    let LanguageMatch = false;
    if (isLanguageFilterConfigured) {
      if (languageFilterArray?.length > 0) {
        for (const language of languageFilterArray) {
          if (data.dctermsLanguage?.indexOf(language) !== -1) {
            LanguageMatch = true;
            data.languageFilterValues.push(language);
            break;
          }
        }
      } else {
        data.languageFilterValues = [...data.dctermsLanguage];
      }
    }
    // Audience new flow logic to check map
    const checkDocsWithoutAudience =
      colToDisplay?.indexOf(data.dctermsType) !== -1 &&
      (isLanguageFilterConfigured && languageFilterArray?.length > 0
        ? LanguageMatch
        : true);
    // markOld:  Audience existing logic to check map
    const checkAudienceForDocs =
      this.checkAudience(
        data,
        this.literaturePageConfig?.segmentOverrideList
      ) && checkDocsWithoutAudience;

    // switch logic based on flags at channel level
    const checkMapping = this.literatureConfigService.checkAudienceFromOverrideList()
      ? checkDocsWithoutAudience
      : checkAudienceForDocs;

    if (checkMapping) {
      return true;
    }
    return false;
  }

  private productDataMergeInitializer(ppssListing): void {
    for (const productData of ppssListing) {
      // to map product data at fund ID level
      // WDE-5738 - EFT fund id 2545 is duplicate so we need to check weather fund id already exist in dataMap or not
      if (
        this.dataMap.has(productData.fundId) &&
        this.literatureConfigService.isCanada
      ) {
        const existingProductData = this.dataMap.get(productData.fundId);
        if (productData.shareClasses.length) {
          existingProductData.shareClasses.push(...productData.shareClasses);
        }
        this.dataMap.set(existingProductData.fundId, existingProductData);
      } else {
        this.dataMap.set(productData.fundId, productData);
      }
      const fundUmbrella = productData.fundUmbrella;
      // to map product data at fundUmberella level
      if (fundUmbrella) {
        if (this.fundUmbrellaMap.has(fundUmbrella)) {
          const ppssArray = this.fundUmbrellaMap.get(fundUmbrella);
          ppssArray.push(productData);
          this.fundUmbrellaMap.set(fundUmbrella, ppssArray);
        } else {
          this.fundUmbrellaMap.set(fundUmbrella, [productData]);
        }
      }
    }
  }

  private matchOnFundId(
    frkOneTISNumber: string[],
    data: LiteratureDocument,
    litSet: Set<string>
  ): boolean {
    data.multipleProductsForSearch = {
      fundNames: [],
    };
    // lit api records based on onetis number which can be multiple with one document
    if (frkOneTISNumber.length > 0) {
      for (const element of frkOneTISNumber) {
        // if site is US then incase of multiple onetis number only add document for oneTis number
        // To avoid duplicate records

        if (!this.isInternational) {
          if (litSet.has(data.docId)) {
            break;
          }
          // to store multiple products data associated to single literature document for 'SEARCH BY KEYWORD' filter
          data.multipleProductsForSearch.fundIds = cloneDeep(frkOneTISNumber);
          frkOneTISNumber.forEach((el) => {
            if (this.dataMap.get(el?.trim() as FundId)?.shareClasses) {
              data.multipleProductsForSearch.fundNames.push(
                this.dataMap.get(el?.trim() as FundId)?.fundName
              );
              data.multipleProductsForSearch.identifiers = this.getKeyIdentifiers(
                this.dataMap.get(el?.trim() as FundId)?.shareClasses,
                data.multipleProductsForSearch.identifiers
                  ? data.multipleProductsForSearch.identifiers
                  : this.initializeIdentifier()
              );
            }
          });
        }
        const productData: Product = this.dataMap.get(
          element?.trim() as FundId
        );
        if (
          this.dataMap.has(element?.trim() as FundId) &&
          productData.shareClasses?.length > 0
        ) {
          // litSet is only specific to US
          litSet?.add(data.docId);
          this.matchOnShareClass(data, productData);
        }
      }
      return true;
    }
    return false;
  }

  private matchOnFundUmbrella(data: LiteratureDocument): boolean {
    if (data.frkFundGroupCode) {
      const fundUmbrella = this.fundUmbrellaMap.get(
        data.frkFundGroupCode.trim()
      );
      if (fundUmbrella) {
        fundUmbrella.forEach((productData) => {
          if (productData.shareClasses?.length > 0) {
            this.matchOnShareClass(data, productData);
          }
        });
        return true;
      }
    }
    return false;
  }

  private matchOnShareClass(data: LiteratureDocument, productData: any): void {
    const matchOn = this.literatureConfigService
      .getDocumentMappingConfiguration()
      .get(data.dctermsType);
    if (
      (!matchOn && data.frkShareClassCode?.length > 1) ||
      (DocumentMatchCondition.FUND_SHARE_CLASS === matchOn &&
        data.frkShareClassCode?.length > 1)
    ) {
      for (const shareClassCode of data.frkShareClassCode) {
        const clonedLitData = cloneDeep(data);
        clonedLitData.frkShareClassCode = [shareClassCode];
        this.loadShareClassData(
          clonedLitData,
          productData,
          this.listingData,
          !(matchOn === DocumentMatchCondition.FUND_ID)
        );
      }
    } else {
      this.loadShareClassData(
        cloneDeep(data),
        productData,
        this.listingData,
        !(matchOn === DocumentMatchCondition.FUND_ID)
      );
    }
  }

  private loadShareClassData(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    showShareClass: boolean
  ): void {
    this.checkShareClassAvailablity(litData, ppssData, showShareClass);

    // TODO:- Can be removed as identifiers are not required for Smarsh bot
    if (litData.allShareClass.length > 0) {
      litData.identifiers = this.getKeyIdentifiers(
        litData.allShareClass,
        this.initializeIdentifier()
      );
    } else {
      litData.identifiers = this.getKeyIdentifiers(
        ppssData.shareClasses,
        this.initializeIdentifier()
      );
    }
    this.loadDocumentList(litData, ppssData, listingData);
  }

  private checkShareClassAvailablity(
    litData: LiteratureDocument,
    ppssData: any,
    showShareClass = false
  ) {
    litData.allShareClass = [];
    if (litData.frkShareClassCode?.length > 0) {
      const shareClasses: ShareClass[] = ppssData.shareClasses;
      let found = false;
      let isPrimaryShareClass = false;
      for (const shareclass of shareClasses) {
        isPrimaryShareClass = false;
        const pdsShareclassCode: ShareClassCode =
          shareclass.identifiers.shareClassCode;
        found = false;
        litData.frkShareClassCode.forEach((litShareClassCode: string) => {
          if (
            pdsShareclassCode === Z_SHARECLASS &&
            litShareClassCode.toLowerCase() === ADVISOR
          ) {
            litShareClassCode = Z_SHARECLASS;
          }
          if (
            this.mapRomanAndNumericShareClass(litShareClassCode) ===
            this.mapRomanAndNumericShareClass(pdsShareclassCode)
          ) {
            litData.shareclass = shareclass;

            // WDE-5738:
            // if isEtfClass is true it means its a hybrid fund.
            if (shareclass && shareclass.isEtfClass) {
              // ETF do not have shareClassFeeStructure, so we need to display shareClassCode in the dropdown.
              shareclass.shareClassFeeStructure = shareclass.shareClassCode;
            }

            // to push all shareclasses associated with a particular document to the literature document object
            litData.allShareClass.push(shareclass);
            found = true;
          }
        });
        if (found) {
          isPrimaryShareClass = shareclass.primaryShareClass;
          // break;
        }
      }
      // to display documnet on fund id level when configured from BR on PPSS detail page
      if (!showShareClass && found) {
        // show share class name when document is tagged with only one share class
        // to hide share class name when document is tagged to primary share class
        if (litData.frkShareClassCode?.length === 1 && !isPrimaryShareClass) {
          litData.showShareClass = true;
        } else {
          litData.showShareClass = false;
        }
      } else {
        litData.showShareClass =
          litData.frkShareClassCode?.length === 1 ? true : false;
      }
    }
  }

  private mapRomanAndNumericShareClass(shareClass: string): string {
    if (shareClass === 'I') {
      return '1';
    }
    if (shareClass === 'II') {
      return '2';
    }
    if (shareClass === 'Primary') {
      return 'P';
    }
    if (shareClass === 'Service') {
      return 'S';
    }
    return shareClass;
  }

  private getKeyIdentifiers(
    shareClasses: ShareClass[],
    identifier // TODO need type
  ): KeyIdentifiers {
    for (const shareClass of shareClasses) {
      identifier.isin.push(shareClass.identifiers.isin);
      identifier.bloomberg.push(shareClass.identifiers.bloomberg);
      identifier.cusip.push(shareClass.identifiers.cusip);
      identifier.reuterSystem.push(shareClass.identifiers.reuterSystem);
      identifier.sedol.push(shareClass.identifiers.sedol);
      identifier.taid.push(shareClass.identifiers.taid);
      identifier.shareClassFeeStructure.push(shareClass.shareClassFeeStructure);
      identifier.shareClassCurrency.push(shareClass.shareClassCurrency);
      identifier.distributionIndicator.push(shareClass.distributionIndicator);
      identifier.dividendFrequency.push(shareClass.dividendFrequency);
      identifier.wkn.push(shareClass.identifiers.wkn);
      identifier.vln.push(shareClass.identifiers.vln);
      identifier.mexid.push(shareClass.identifiers.mexId);
      identifier.rfi.push(shareClass.identifiers.rfi);
      identifier.apir.push(shareClass.identifiers.apir);
      identifier.arsn.push(shareClass.identifiers.arsn);
      identifier.adm.push(shareClass.identifiers.adm);
      identifier.dsc.push(shareClass.identifiers.dsc);
      identifier.ticker.push(shareClass.identifiers.ticker);
      identifier.iress.push(shareClass.identifiers.iress);
      identifier.iressinavcode.push(shareClass.identifiers.iressinavCode);
      identifier.anbimacode.push(shareClass.identifiers.anbimaCode);
      identifier.intradaynav.push(shareClass.identifiers.intradayNav);
      identifier.fundbmticker.push(shareClass.identifiers.fundbmTicker);
    }
    return identifier;
  }

  private initializeIdentifier(): KeyIdentifiers {
    return {
      isin: [],
      bloomberg: [],
      cusip: [],
      reuterSystem: [],
      sedol: [],
      taid: [],
      shareClassFeeStructure: [],
      vln: [],
      wkn: [],
      mexid: [],
      rfi: [],
      apir: [],
      arsn: [],
      adm: [],
      dsc: [],
      ticker: [],
      iress: [],
      iressinavcode: [],
      anbimacode: [],
      intradaynav: [],
      fundbmticker: [],
      distributionIndicator: [],
      dividendFrequency: [],
      shareClassCurrency: [],
    };
  }

  private loadDocumentList(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[]
  ) {
    if (this.isInternational && this.isShareClassValid(litData)) {
      if (
        this.literatureConfigService.isCanada &&
        litData?.availableForLoggedInUser === NoYesFlag.YES
      ) {
        if (this.isLoggedIn) {
          this.addToList(litData, ppssData, listingData);
        }
      } else {
        this.addToList(litData, ppssData, listingData);
      }
    } else if (!this.isInternational) {
      if (this.isShareClassValid(litData)) {
        // this.isDocForLoggedIn(litData);
        this.addToList(litData, ppssData, listingData);
      }
    }
  }

  private isShareClassValid(litData: LiteratureDocument): boolean {
    if (litData.frkShareClassCode?.length === 0 || litData.shareclass) {
      return true;
    }
    return false;
  }

  private addToList(
    litData: LiteratureDocument,
    ppssData: Product,
    listingData: LiteratureDocumentListingData[]
  ) {
    this.literatureDataService.mapLiteratureUrl(litData);
    const literatureDocumentListingData: LiteratureDocumentListingData = this.getLiteratureDocumentListingData(
      litData,
      ppssData
    );

    // literatureDocumentListingData.literatureData.rowId = this.getUniqueID(
    //   literatureDocumentListingData
    // );

    literatureDocumentListingData.literatureData.fundNameGenerated = this.getFundNameGenerated(
      literatureDocumentListingData
    );
    listingData.push(literatureDocumentListingData);
  }

  private getLiteratureDocumentListingData(
    litData: LiteratureDocument,
    productData: Product
  ): LiteratureDocumentListingData {
    return {
      literatureData: litData,
      product: productData,
    };
  }

  public getFundNameGenerated(data: LiteratureDocumentListingData): string {
    const fundName = this.isInternational
      ? nameGenerator(
          data,
          this.translateService,
          this.literatureConfigService.isCanada
        )
      : fundNameGenerator(data, this.translateService);
    return fundName;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
